/**
 * 容园机械协议回报率
 *
 * 命令（Feature command table）：
 * - FEA_CMD_SET_REPORT = 0x03
 * - FEA_CMD_GET_REPORT = 0x83
 *
 * SET_REPORT 报文：Byte0=0x03, Byte1=0x00, **Byte2=Report**
 * GET_REPORT 回包：Byte1=0x00，Byte2=Report（HID 缓冲 response[2]）
 *
 * USB / 2.4G / 蓝牙统一 7 档。
 *
 * Report 字节与 Hz 为查表映射（与公版 X87S 驱动实测一致）：
 * 8→8K, 1→4K, 2→2K, 3→1K, 4→500, 5→250, 6→125
 */

export interface RongyuanReportRateOption {
  /** 协议 Report 字节 */
  protocolByte: number;
  /** 轮询间隔（毫秒） */
  intervalMs: number;
  /** 回报率 Hz */
  hz: number;
  /** UI 展示 */
  label: string;
}

/** X87S 公版 7 档 Report 字节表（USB / 2.4G / 蓝牙共用） */
export const RONGYUAN_WIRED_REPORT_RATE_OPTIONS: readonly RongyuanReportRateOption[] =
  [
    { protocolByte: 8, intervalMs: 0.125, hz: 8000, label: "8000Hz" },
    { protocolByte: 1, intervalMs: 0.25, hz: 4000, label: "4000Hz" },
    { protocolByte: 2, intervalMs: 0.5, hz: 2000, label: "2000Hz" },
    { protocolByte: 3, intervalMs: 1, hz: 1000, label: "1000Hz" },
    { protocolByte: 4, intervalMs: 2, hz: 500, label: "500Hz" },
    { protocolByte: 5, intervalMs: 4, hz: 250, label: "250Hz" },
    { protocolByte: 6, intervalMs: 8, hz: 125, label: "125Hz" },
  ];

/** @deprecated 与有线档位相同，保留别名兼容旧引用 */
export const RONGYUAN_WIRELESS_REPORT_RATE_OPTIONS: readonly RongyuanReportRateOption[] =
  RONGYUAN_WIRED_REPORT_RATE_OPTIONS;

export const RONGYUAN_REPORT_RATE_OPTIONS: readonly RongyuanReportRateOption[] =
  RONGYUAN_WIRED_REPORT_RATE_OPTIONS;

export const RONGYUAN_SUPPORTED_REPORT_RATE_HZ: readonly number[] =
  RONGYUAN_REPORT_RATE_OPTIONS.map((item) => item.hz);

/** @deprecated 与 RONGYUAN_SUPPORTED_REPORT_RATE_HZ 相同 */
export const RONGYUAN_WIRELESS_SUPPORTED_REPORT_RATE_HZ: readonly number[] =
  RONGYUAN_SUPPORTED_REPORT_RATE_HZ;

const HZ_TO_BYTE = new Map(
  RONGYUAN_REPORT_RATE_OPTIONS.map((item) => [item.hz, item.protocolByte])
);
const BYTE_TO_HZ = new Map(
  RONGYUAN_REPORT_RATE_OPTIONS.map((item) => [item.protocolByte, item.hz])
);

const REPORT_BYTE_SET = new Set(
  RONGYUAN_REPORT_RATE_OPTIONS.map((item) => item.protocolByte)
);

/** @deprecated 各链路映射已统一，wired 参数不再影响行为 */
export function isRongyuanWiredReportRateLink(_wired?: boolean): boolean {
  return true;
}

/** 是否为有效 Report 字节 */
export function isRongyuanReportRateProtocolByte(
  byte: number,
  _wired = true
): boolean {
  return REPORT_BYTE_SET.has(byte);
}

/**
 * 从 GET_REPORT / 2.4G GETINDATA 响应解析 Report 字节。
 * 协议 IN：GET_REPORT(0x83) | 0x00 | Report | …
 */
export function parseRongyuanReportRateProtocolByte(
  response: ArrayLike<number>,
  _wired = true,
  maxScan = 16
): number {
  const valid = (byte: number) => isRongyuanReportRateProtocolByte(byte);
  const cmd = response[0] ?? 0;

  if (cmd === 0x83) {
    const reportAt2 = response[2] ?? 0;
    if (valid(reportAt2)) return reportAt2;
  }

  const byte1 = response[1] ?? 0;
  if (valid(byte1)) return byte1;

  const limit = Math.min(response.length, maxScan);
  for (let i = 2; i < limit; i++) {
    const byte = response[i] ?? 0;
    if (valid(byte)) return byte;
  }
  return cmd === 0x83 ? (response[2] ?? byte1) : byte1;
}

/** Hz → Report 字节 */
export function rongyuanHzToReportProtocolByte(
  hz: number,
  _wired = true
): number | undefined {
  return HZ_TO_BYTE.get(hz);
}

/** Report 字节 → Hz */
export function rongyuanReportProtocolByteToHz(
  protocolByte: number,
  _wired = true
): number {
  const direct = BYTE_TO_HZ.get(protocolByte);
  if (direct != null) return direct;
  return RONGYUAN_PERFORMANCE_DEFAULTS.pollingRateHz;
}

/** 校验 Hz 是否为支持的回报率 */
export function isRongyuanSupportedReportRateHz(
  hz: number,
  _wired = true
): boolean {
  return HZ_TO_BYTE.has(hz);
}

/** 官方驱动默认性能配置（出厂/未读取设备时回显） */
export const RONGYUAN_PERFORMANCE_DEFAULTS = {
  pollingRateHz: 1000,
  debounce: 1,
  lowPowerMode: false,
  sleepTime24Minutes: 2,
  sleepTimeBtMinutes: 2,
  deepSleepTimeBtMinutes: 28,
} as const;

/** 协议休眠时间默认值（秒，SET/GET_SLEEPTIME Byte8-15） */
export const RONGYUAN_SLEEP_TIME_DEFAULTS_SEC = {
  time_bt: RONGYUAN_PERFORMANCE_DEFAULTS.sleepTimeBtMinutes * 60,
  time_24: RONGYUAN_PERFORMANCE_DEFAULTS.sleepTime24Minutes * 60,
  deepTime_bt: RONGYUAN_PERFORMANCE_DEFAULTS.deepSleepTimeBtMinutes * 60,
  deepTime_24: RONGYUAN_PERFORMANCE_DEFAULTS.deepSleepTimeBtMinutes * 60,
};

export function rongyuanSleepSecondsToMinutes(seconds: number): number {
  if (seconds <= 0) return 0;
  return Math.round(seconds / 60);
}

export function rongyuanSleepMinutesToSeconds(minutes: number): number {
  if (minutes <= 0) return 0;
  return minutes * 60;
}

export const RONGYUAN_DEBOUNCE_UI_MIN = 1;
export const RONGYUAN_DEBOUNCE_UI_MAX = 10;

export function rongyuanDebounceProtocolByteToMs(protocolByte: number): number {
  const byte = Math.max(0, Math.min(255, Math.round(protocolByte)));
  if (byte === 0) return 2;
  return byte * 2 + 3;
}

export function rongyuanDebounceUiToProtocolByte(uiLevel: number): number {
  const level = Math.round(uiLevel);
  return Math.min(
    RONGYUAN_DEBOUNCE_UI_MAX,
    Math.max(RONGYUAN_DEBOUNCE_UI_MIN, level)
  );
}

export function rongyuanDebounceProtocolByteToUi(protocolByte: number): number {
  const byte = Math.round(protocolByte);
  if (byte <= 0) return RONGYUAN_DEBOUNCE_UI_MIN;
  return Math.min(
    RONGYUAN_DEBOUNCE_UI_MAX,
    Math.max(RONGYUAN_DEBOUNCE_UI_MIN, byte)
  );
}
